import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Spinner, Badge, InputGroup, Alert } from "react-bootstrap";
import { useAuth } from "../../auth/AuthContext";
import ModalConfirmar from "../../components/ModalConfirmar";
import * as cuentasApi from "../../api/cuentas";
import * as bancosApi from "../../api/bancos";
import * as monedasApi from "../../api/monedas";
import { formatoNumero } from "../../utils/moneda";

const UMBRAL_ALERTA = 0.8;
const ANCHO_GRANDE_MQ = "(min-width: 992px)";

/** Preferencia de vista (lista/tarjetas/grid) persistida por usuario y por tipo de cuenta en localStorage. */
function useVistaCuentas(email, tipo) {
  const key = email ? `fs_vista_cuentas_${tipo}_${email}` : null;
  const [vista, setVistaState] = useState(() => (key && localStorage.getItem(key)) || "tarjetas");

  const setVista = (v) => {
    setVistaState(v);
    if (key) localStorage.setItem(key, v);
  };

  return [vista, setVista];
}

/** true si la pantalla es lo bastante ancha para la vista de tarjetas compactas en grilla. */
function useAnchoGrande() {
  const [anchoGrande, setAnchoGrande] = useState(() => window.matchMedia(ANCHO_GRANDE_MQ).matches);
  useEffect(() => {
    const mq = window.matchMedia(ANCHO_GRANDE_MQ);
    const actualizar = () => setAnchoGrande(mq.matches);
    mq.addEventListener("change", actualizar);
    window.addEventListener("resize", actualizar);
    return () => {
      mq.removeEventListener("change", actualizar);
      window.removeEventListener("resize", actualizar);
    };
  }, []);
  return anchoGrande;
}

/** Página de Cuentas Bancarias o de Tarjetas de Crédito (según `tipo`), ambas comparten esta implementación. */
export default function CuentasBancariasBase({ tipo }) {
  const esTarjetaPagina = tipo === "TarjetaCredito";
  const titulo = esTarjetaPagina ? "Tarjetas de Crédito" : "Cuentas Bancarias";
  const { esAdministrador, usuario } = useAuth();
  const [cuentas, setCuentas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [aEliminar, setAEliminar] = useState(null);
  const [aPagar, setAPagar] = useState(null);
  const [vista, setVista] = useVistaCuentas(usuario?.email, tipo);
  const [mensajeInfo, setMensajeInfo] = useState(null);
  const [modoReordenar, setModoReordenar] = useState(false);
  const [ordenTrabajo, setOrdenTrabajo] = useState([]);
  const [guardandoOrden, setGuardandoOrden] = useState(false);
  const anchoGrande = useAnchoGrande();
  const vistaEfectiva = vista === "grid" && !anchoGrande ? "tarjetas" : vista;

  const cargar = async () => {
    setCargando(true);
    try {
      const todas = await cuentasApi.listarCuentas();
      setCuentas(todas.filter((c) => c.tipo === tipo));
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, [tipo]); // eslint-disable-line react-hooks/exhaustive-deps

  const abrirNueva = () => { setEditando(null); setShowModal(true); };
  const abrirEditar = (c) => { setEditando(c); setShowModal(true); };

  const confirmarEliminar = async () => {
    const { eliminadaCompleto, mensaje } = await cuentasApi.eliminarCuenta(aEliminar.cuentaId);
    setAEliminar(null);
    if (!eliminadaCompleto && mensaje) setMensajeInfo(mensaje);
    cargar();
  };

  const cantidadReordenable = cuentas.filter((c) => !c.esPrincipal).length;

  const abrirReordenar = () => {
    setOrdenTrabajo([...cuentas]);
    setModoReordenar(true);
  };

  // La cuenta Principal nunca se mueve ni se puede saltar por encima de ella: siempre queda primera.
  const moverFila = (idx, delta) => {
    setOrdenTrabajo((prev) => {
      const destino = idx + delta;
      if (destino < 0 || destino >= prev.length) return prev;
      if (prev[idx].esPrincipal || prev[destino].esPrincipal) return prev;
      const nuevo = [...prev];
      [nuevo[idx], nuevo[destino]] = [nuevo[destino], nuevo[idx]];
      return nuevo;
    });
  };

  const guardarOrden = async () => {
    setGuardandoOrden(true);
    try {
      await cuentasApi.guardarOrden(tipo, ordenTrabajo.map((c) => c.cuentaId));
      setModoReordenar(false);
      await cargar();
    } finally {
      setGuardandoOrden(false);
    }
  };

  const AccionesCuenta = ({ c }) => !esAdministrador && (
    <>
      {esTarjetaPagina && (
        <Button size="sm" variant="outline-success" className="me-1" title="Registrar pago" onClick={() => setAPagar(c)}>
          <i className="bi bi-cash-coin"></i>
        </Button>
      )}
      <Button size="sm" variant="outline-primary" className="me-1" onClick={() => abrirEditar(c)}>
        <i className="bi bi-pencil"></i>
      </Button>
      <Button size="sm" variant="outline-danger" onClick={() => setAEliminar(c)}>
        <i className="bi bi-trash"></i>
      </Button>
    </>
  );

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h4 className="mb-0">{titulo} {esAdministrador && <Badge bg="secondary" className="ms-2">Solo lectura</Badge>}</h4>
        <div className="d-flex gap-2">
          <div className="btn-group" role="group">
            {anchoGrande ? (
              <>
                <Button size="sm" variant={vista === "lista" ? "primary" : "outline-primary"} title="Vista lista" onClick={() => setVista("lista")}>
                  <i className="bi bi-list-ul"></i>
                </Button>
                <Button size="sm" variant={vista === "tarjetas" ? "primary" : "outline-primary"} title="Vista tarjetas" onClick={() => setVista("tarjetas")}>
                  <i className="bi bi-card-list"></i>
                </Button>
                <Button size="sm" variant={vista === "grid" ? "primary" : "outline-primary"} title="Vista tarjetas compactas" onClick={() => setVista("grid")}>
                  <i className="bi bi-grid-3x3-gap-fill"></i>
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" variant={vistaEfectiva === "tarjetas" ? "primary" : "outline-primary"} title="Vista tarjetas" onClick={() => setVista("tarjetas")}>
                  <i className="bi bi-card-list"></i>
                </Button>
                <Button size="sm" variant={vistaEfectiva === "lista" ? "primary" : "outline-primary"} title="Vista lista" onClick={() => setVista("lista")}>
                  <i className="bi bi-list-ul"></i>
                </Button>
              </>
            )}
          </div>
          {!esAdministrador && cantidadReordenable > 1 && (
            <Button size="sm" variant="outline-secondary" onClick={abrirReordenar}>
              <i className="bi bi-arrow-down-up me-1"></i>Reordenar
            </Button>
          )}
          {!esAdministrador && (
            <Button size="sm" variant="primary" onClick={abrirNueva}>
              <i className="bi bi-plus-lg me-1"></i>{esTarjetaPagina ? "Nueva tarjeta" : "Nueva cuenta"}
            </Button>
          )}
        </div>
      </div>

      {mensajeInfo && (
        <Alert variant="info" dismissible onClose={() => setMensajeInfo(null)}>
          {mensajeInfo}
        </Alert>
      )}

      {cargando ? <Spinner animation="border" /> : modoReordenar ? (
        <div className="fs-card p-0 overflow-hidden">
          <div className="p-3 pb-2 text-muted small">
            <i className="bi bi-info-circle me-1"></i>
            La {esTarjetaPagina ? "tarjeta" : "cuenta"} principal siempre queda primera. Usa las flechas para ordenar el resto.
          </div>
          <div className="list-group list-group-flush">
            {ordenTrabajo.map((c, idx) => (
              <div key={c.cuentaId} className="list-group-item d-flex align-items-center gap-3 py-3">
                <i className={`bi ${esTarjetaPagina ? "bi-credit-card-2-front" : "bi-bank"} text-muted fs-5`}></i>
                <div className="flex-grow-1">
                  <div className="fw-semibold">{c.banco}</div>
                  <div className="text-muted small">{c.alias || c.moneda}</div>
                </div>
                {c.esPrincipal ? (
                  <Badge bg="secondary"><i className="bi bi-star-fill me-1"></i>Principal</Badge>
                ) : (
                  <div className="d-flex gap-1">
                    <Button size="sm" variant="outline-secondary" disabled={idx === 0 || ordenTrabajo[idx - 1].esPrincipal}
                      onClick={() => moverFila(idx, -1)} aria-label="Subir">
                      <i className="bi bi-arrow-up"></i>
                    </Button>
                    <Button size="sm" variant="outline-secondary" disabled={idx === ordenTrabajo.length - 1}
                      onClick={() => moverFila(idx, 1)} aria-label="Bajar">
                      <i className="bi bi-arrow-down"></i>
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="d-flex justify-content-end gap-2 p-3">
            <Button variant="outline-secondary" onClick={() => setModoReordenar(false)} disabled={guardandoOrden}>Cancelar</Button>
            <Button variant="primary" onClick={guardarOrden} disabled={guardandoOrden}>
              {guardandoOrden ? <Spinner size="sm" animation="border" /> : "Guardar orden"}
            </Button>
          </div>
        </div>
      ) : vistaEfectiva === "grid" ? (
        <div className="cuentas-grid">
          {cuentas.map((c) => {
            const porcentajeUsado = c.limiteCredito > 0 ? c.saldoActual / c.limiteCredito : null;
            const cercaDelLimite = porcentajeUsado !== null && porcentajeUsado >= UMBRAL_ALERTA;
            const disponible = Number(c.limiteCredito || 0) - Number(c.saldoActual || 0);
            return (
              <div key={c.cuentaId} className="fs-card cuenta-card-compacta">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <i className={`bi ${esTarjetaPagina ? "bi-credit-card-2-front" : "bi-bank"} text-muted fs-5`}></i>
                    <div>
                      <div className="fw-semibold">{c.banco}</div>
                      <div className="text-muted small">{c.alias || c.moneda}{esAdministrador && c.usuario ? ` · ${c.usuario}` : ""}</div>
                    </div>
                  </div>
                  {!esTarjetaPagina && c.esPrincipal && <i className="bi bi-star-fill text-fs-alerta" title="Cuenta principal"></i>}
                </div>

                {esTarjetaPagina ? (
                  <>
                    <div className="text-muted small">Monto usado</div>
                    <div className="fw-bold text-fs-gasto" style={{ fontSize: "1.3rem" }}>
                      {c.simbolo}{formatoNumero(c.saldoActual)}
                    </div>
                    <div className="small mt-1 d-flex flex-wrap align-items-center gap-2">
                      <span className="text-fs-info">Línea {c.simbolo}{formatoNumero(c.limiteCredito || 0)}</span>
                      <span className="text-fs-ingreso">Disponible {c.simbolo}{formatoNumero(disponible)}</span>
                      <Badge bg={cercaDelLimite ? "danger" : "secondary"}>{(porcentajeUsado * 100).toFixed(0)}%</Badge>
                    </div>
                  </>
                ) : (
                  <div className="fw-bold" style={{ fontSize: "1.3rem" }}>
                    {c.simbolo}{formatoNumero(c.saldoActual)}
                  </div>
                )}

                <div className="d-flex justify-content-between align-items-center mt-2">
                  <Badge bg={c.activo ? "success" : "secondary"}>{c.activo ? "Activa" : "Inactiva"}</Badge>
                  <div><AccionesCuenta c={c} /></div>
                </div>
              </div>
            );
          })}
          {!cuentas.length && <div className="text-center text-muted py-4">No hay {esTarjetaPagina ? "tarjetas" : "cuentas"} registradas.</div>}
        </div>
      ) : (
        <div className="fs-card p-0 overflow-hidden">
          <Table responsive hover className={`table-fs mb-0 align-middle ${vistaEfectiva === "tarjetas" ? "table-fs-cards" : ""}`}>
            <thead>
              <tr>
                {esAdministrador && <th>Usuario</th>}
                <th>Banco</th>
                <th>Alias</th>
                <th>Moneda</th>
                {esTarjetaPagina ? (
                  <>
                    <th className="text-end">Monto usado</th>
                    <th className="text-end">Línea de crédito</th>
                    <th className="text-end">Monto disponible</th>
                    <th className="text-end">% Usado</th>
                  </>
                ) : (
                  <>
                    <th className="text-end">Saldo actual</th>
                    <th>Principal</th>
                  </>
                )}
                <th>Estado</th>
                {!esAdministrador && <th></th>}
              </tr>
            </thead>
            <tbody>
              {cuentas.map((c) => {
                const porcentajeUsado = c.limiteCredito > 0 ? c.saldoActual / c.limiteCredito : null;
                const cercaDelLimite = porcentajeUsado !== null && porcentajeUsado >= UMBRAL_ALERTA;
                const disponible = Number(c.limiteCredito || 0) - Number(c.saldoActual || 0);
                return (
                  <tr key={c.cuentaId}>
                    {esAdministrador && <td data-label="Usuario">{c.usuario}</td>}
                    <td data-label="Banco">
                      <i className={`bi ${esTarjetaPagina ? "bi-credit-card-2-front" : "bi-bank"} me-2 text-muted`}></i>
                      {c.banco}
                    </td>
                    <td data-label="Alias">{c.alias || "—"}</td>
                    <td data-label="Moneda">{c.moneda}</td>
                    {esTarjetaPagina ? (
                      <>
                        <td data-label="Monto usado" className="text-end fw-semibold text-fs-gasto">
                          {c.simbolo}{formatoNumero(c.saldoActual)}
                        </td>
                        <td data-label="Línea de crédito" className="text-end fw-semibold text-fs-info">
                          {c.simbolo}{formatoNumero(c.limiteCredito || 0)}
                        </td>
                        <td data-label="Monto disponible" className="text-end fw-semibold text-fs-ingreso">
                          {c.simbolo}{formatoNumero(disponible)}
                        </td>
                        <td data-label="% Usado" className="text-end">
                          {porcentajeUsado !== null ? (
                            <Badge bg={cercaDelLimite ? "danger" : "secondary"}>
                              {(porcentajeUsado * 100).toFixed(0)}%
                            </Badge>
                          ) : "—"}
                        </td>
                      </>
                    ) : (
                      <>
                        <td data-label="Saldo" className="text-end fw-semibold">
                          {c.simbolo}{formatoNumero(c.saldoActual)}
                        </td>
                        <td data-label="Principal">
                          {c.esPrincipal && <i className="bi bi-star-fill text-fs-alerta" title="Cuenta principal"></i>}
                        </td>
                      </>
                    )}
                    <td data-label="Estado">
                      <Badge bg={c.activo ? "success" : "secondary"}>{c.activo ? "Activa" : "Inactiva"}</Badge>
                    </td>
                    {!esAdministrador && (
                      <td data-label="Acciones" className="text-end">
                        <AccionesCuenta c={c} />
                      </td>
                    )}
                  </tr>
                );
              })}
              {!cuentas.length && (
                <tr><td colSpan={10} className="text-center text-muted py-4">No hay {esTarjetaPagina ? "tarjetas" : "cuentas"} registradas.</td></tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      <ModalCuenta show={showModal} onHide={() => setShowModal(false)} cuenta={editando} tipo={tipo} onGuardado={() => { setShowModal(false); cargar(); }} />
      <ModalRegistrarPago cuenta={aPagar} onHide={() => setAPagar(null)} onGuardado={() => { setAPagar(null); cargar(); }} />
      <ModalConfirmar
        show={!!aEliminar}
        titulo={esTarjetaPagina ? "Eliminar tarjeta de crédito" : "Eliminar cuenta bancaria"}
        mensaje={`¿Seguro que deseas eliminar "${aEliminar?.alias || aEliminar?.banco}"?`}
        onConfirmar={confirmarEliminar}
        onCancelar={() => setAEliminar(null)}
      />
    </div>
  );
}

function ModalCuenta({ show, onHide, cuenta, tipo, onGuardado }) {
  const esTarjeta = tipo === "TarjetaCredito";
  const [bancos, setBancos] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const [form, setForm] = useState({
    bancoId: "", monedaId: "", alias: "", numeroCuenta: "", saldoInicial: "",
    activo: true, esPrincipal: false, limiteCredito: "",
  });
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (show) {
      bancosApi.listarBancos().then((r) => setBancos(r.filter((b) => b.activo)));
      monedasApi.listarMonedas().then((r) => setMonedas(r.filter((m) => m.activo)));
      setForm(cuenta
        ? {
            bancoId: cuenta.bancoId, monedaId: cuenta.monedaId, alias: cuenta.alias || "", numeroCuenta: cuenta.numeroCuenta || "",
            saldoInicial: "", activo: cuenta.activo, esPrincipal: cuenta.esPrincipal, limiteCredito: cuenta.limiteCredito ?? "",
          }
        : { bancoId: "", monedaId: "", alias: "", numeroCuenta: "", saldoInicial: "", activo: true, esPrincipal: false, limiteCredito: "" });
    }
  }, [show, cuenta]);

  const actualizar = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await cuentasApi.upsertCuentaPropia({
        cuentaId: cuenta?.cuentaId ?? null,
        bancoId: Number(form.bancoId),
        monedaId: Number(form.monedaId),
        alias: form.alias,
        numeroCuenta: form.numeroCuenta,
        saldoActual: cuenta ? cuenta.saldoActual : Number(form.saldoInicial || 0),
        activo: !!form.activo,
        esPrincipal: !!form.esPrincipal,
        tipo,
        limiteCredito: esTarjeta ? Number(form.limiteCredito || 0) : null,
      });
      onGuardado();
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>
          {cuenta ? (esTarjeta ? "Editar tarjeta de crédito" : "Editar cuenta bancaria") : (esTarjeta ? "Nueva tarjeta de crédito" : "Nueva cuenta bancaria")}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Banco</Form.Label>
            <Form.Select value={form.bancoId} onChange={actualizar("bancoId")} required>
              <option value="">Selecciona...</option>
              {bancos.map((b) => <option key={b.bancoId} value={b.bancoId}>{b.nombre}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Moneda</Form.Label>
            <Form.Select value={form.monedaId} onChange={actualizar("monedaId")} required>
              <option value="">Selecciona...</option>
              {monedas.map((m) => <option key={m.monedaId} value={m.monedaId}>{m.codigo} — {m.nombre}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Alias (opcional)</Form.Label>
            <Form.Control value={form.alias} onChange={actualizar("alias")} placeholder={esTarjeta ? "Ej. Tarjeta Visa Oro" : "Ej. Cuenta de ahorros"} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Número de {esTarjeta ? "tarjeta" : "cuenta"} (opcional)</Form.Label>
            <Form.Control value={form.numeroCuenta} onChange={actualizar("numeroCuenta")} />
          </Form.Group>
          {esTarjeta && (
            <Form.Group className="mb-3">
              <Form.Label>Línea de crédito</Form.Label>
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control type="number" step="0.01" min="0" value={form.limiteCredito} onChange={actualizar("limiteCredito")} required />
              </InputGroup>
            </Form.Group>
          )}
          {!cuenta && (
            <Form.Group className="mb-3">
              <Form.Label>{esTarjeta ? "Monto usado inicial (opcional)" : "Saldo inicial (opcional)"}</Form.Label>
              <Form.Control type="number" step="0.01" min="0" value={form.saldoInicial} onChange={actualizar("saldoInicial")} placeholder="0.00" />
              <Form.Text className="text-muted">
                {esTarjeta ? "Si ya tienes consumo previo en la tarjeta." : "Con cuánto empieza la cuenta. No cuenta como ingreso del mes."}
              </Form.Text>
            </Form.Group>
          )}
          {!esTarjeta && (
            <Form.Check type="switch" label="Cuenta principal" checked={form.esPrincipal}
              onChange={(e) => setForm({ ...form, esPrincipal: e.target.checked })} />
          )}
          {form.esPrincipal && !esTarjeta && (
            <div className="text-muted small mt-1">Será la cuenta seleccionada por defecto en Dashboard, Ingresos y Gastos.</div>
          )}
          {cuenta && (
            <Form.Check type="switch" label="Activa" className="mt-2" checked={form.activo}
              onChange={(e) => setForm({ ...form, activo: e.target.checked })} />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={guardando}>
            {guardando ? <Spinner size="sm" animation="border" /> : "Guardar"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function ModalRegistrarPago({ cuenta, onHide, onGuardado }) {
  const [monto, setMonto] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { if (cuenta) setMonto(""); }, [cuenta]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await cuentasApi.registrarPagoTarjeta(cuenta.cuentaId, Number(monto));
      onGuardado();
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={!!cuenta} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Registrar pago — {cuenta?.alias || cuenta?.banco}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Monto a abonar</Form.Label>
            <InputGroup>
              <InputGroup.Text>{cuenta?.simbolo}</InputGroup.Text>
              <Form.Control type="number" step="0.01" min="0.01" value={monto} onChange={(e) => setMonto(e.target.value)} required autoFocus />
            </InputGroup>
            <Form.Text className="text-muted">Reduce el monto usado de la tarjeta.</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={guardando}>
            {guardando ? <Spinner size="sm" animation="border" /> : "Registrar"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
